import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  FolderSimple,
  FolderOpen,
  CloudArrowUp, ArrowsClockwise, MicrosoftOutlookLogo,
  Trash, DownloadSimple, Lock, Warning,
  PencilSimple, ArrowsLeftRight, ArrowSquareOut, CaretRight, X,
  ChatTeardropText,
  ClockCounterClockwise, ArrowLeft, EnvelopeOpen, CircleNotch,
  Rows, SquaresFour, Plus, UserPlus, FolderPlus, Robot, Sparkle,
  Microphone, CheckCircle,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import {
  getMsStatus, getMsAuthorizeUrl, disconnectMs,
  listFolder, getDdRoot, uploadFile, downloadFileItem,
  renameItem, moveItem, deleteItem, checkPreviousVersions,
  getEmailFileContent, createFolder,
  getArchiveFolderContext, restoreDrawingVersion,
} from "../api/microsoft";
import MoveFolderPicker from "../components/files/MoveFolderPicker";
import SmartUploadPanel from "../components/files/SmartUploadPanel";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { FileAssistantSidebar } from "../features/files/assistant/FileAssistantSidebar";
import { ConnectScreen } from "../features/files/explorer/ConnectScreen";
import { DD_FOLDER_NAME, fileIcon, fmtDate, fmtSize, isAnalyzable, permissions } from "../features/files/explorer/model";
import { getProjects, updateProject, enableContractor } from "../api/projects";
import { getRecordingsByFolder } from "../api/recordings";
import { sendFileChat, clearFileChat, applyLayoutModification } from "../api/files";
import { compareFromVersionHistory } from "../api/compare";
import { setPendingAnalysisFile } from "../fileTransfer";
import { takePendingFolderRequest } from "../folderRequest";

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ExploreFilesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Prevents the history-restoration effect from double-loading when WE
  // are the ones who just called navigate() (not the browser back button).
  const selfNavigate = useRef(false);
  // Set to true once initExplorer has completed at least once.
  const initialized  = useRef(false);

  // Microsoft backend connection state
  const [msConnected, setMsConnected]   = useState(false);
  const [msCheckDone, setMsCheckDone]   = useState(false);
  const [msConnecting, setMsConnecting] = useState(false);

  // Explorer state
  const [ddRootId, setDdRootId]             = useState(null);
  const [breadcrumbs, setBreadcrumbs]       = useState([]); // [{id,name}] — first entry is DD root
  const [items, setItems]                   = useState([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [emailPreview, setEmailPreview]     = useState({ open: false, html: "", filename: "", loading: false });
  const [projects, setProjects]             = useState([]);

  // Actions
  const [renamingId, setRenamingId]         = useState(null);
  const [renameValue, setRenameValue]       = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId]         = useState(null);
  const [movingItem, setMovingItem]         = useState(null);
  const [uploading, setUploading]           = useState(false);
  const [analyzingId, setAnalyzingId]       = useState(null); // file being loaded into analysis
  const [comparingId, setComparingId]       = useState(null); // file being compared to its previous version
  const [previousVersions, setPreviousVersions] = useState({}); // {drawing_id: {archive_folder_id, archive_folder_url, count}}
  const [folderRecordings, setFolderRecordings] = useState([]); // meeting recordings filed into the current folder

  const fileInputRef = useRef(null);
  const currentFolderId = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].id : null;
  const depth = breadcrumbs.length - 1; // 0 = at DD root
  const parentName = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].name : null;
  // If any breadcrumb is "Completed Projects" we need to offset the protection depth by 1
  const completedOffset = breadcrumbs.some(b => b.name === "Completed Projects") ? 1 : 0;

  const currentProject = useMemo(() => {
    const projectCrumbIdx = 1 + completedOffset;
    const projectCrumb = breadcrumbs[projectCrumbIdx];
    if (!projectCrumb) return null;
    return projects.find((project) => project.onedrive_folder_id === projectCrumb.id) || null;
  }, [breadcrumbs, completedOffset, projects]);

  // ── Archive / Restore state ────────────────────────────────────────────────
  const [archiveContext, setArchiveContext]   = useState(null); // {is_archive_folder, current_drawing_name, …}
  const [restoringId,    setRestoringId]      = useState(null); // item.id currently being restored

  // ── Smart Upload modal state ───────────────────────────────────────────────
  const [smartUploadOpen,    setSmartUploadOpen]    = useState(false);
  const [smartUploadProject, setSmartUploadProject] = useState(null); // project object
  const [smartUploadFiles,   setSmartUploadFiles]   = useState([]);   // pre-seeded files from page drop
  const [pageIsDragging,     setPageIsDragging]     = useState(false);

  // ── Drag-and-drop within the file explorer ─────────────────────────────────
  const [draggingItem,   setDraggingItem]   = useState(null);   // FileItem being dragged
  const [dragOverId,     setDragOverId]     = useState(null);   // folder ID currently hovered

  // ── AI Chat state ──────────────────────────────────────────────────────────
  const [chatOpen, setChatOpen]             = useState(false);  const [viewMode, setViewMode]             = useState(() => localStorage.getItem("exploreViewMode") || "list");
  const [msAccountEmail, setMsAccountEmail] = useState("");
  const [msAccountName, setMsAccountName]   = useState("");
  const [chatMessages, setChatMessages]     = useState([]);
  const [chatInput, setChatInput]           = useState("");
  const [chatLoading, setChatLoading]       = useState(false);
  const [chatSessionId]                     = useState(() => crypto.randomUUID());
  const [pendingDeleteAction, setPendingDeleteAction] = useState(null); // {item_id, item_name, reason}
  const [pendingMoveAction, setPendingMoveAction]     = useState(null); // {item_id, item_name, suggested_folder_name}
  const [pendingCreateFolderAction, setPendingCreateFolderAction] = useState(null); // {folder_name, parent_folder_id, reason}
  const [pendingAddContractorAction, setPendingAddContractorAction] = useState(null); // {contractor_name, project_id, reason}
  // Contractor contact-details modal (shown before executing add_contractor action)
  const [contractorContactModal, setContractorContactModal] = useState(null); // null | {action, contactName, contactEmail}
  // New Folder inline creation
  const [creatingFolder, setCreatingFolder]         = useState(false);
  const [newFolderName, setNewFolderName]           = useState("");
  const [newFolderLoading, setNewFolderLoading]     = useState(false);
  const chatEndRef                          = useRef(null);

  // ── Initialize when signed in ──────────────────────────────────────────────

  // Check server-side MS connection on mount
  useEffect(() => {
    getMsStatus()
      .then((status) => {
        setMsConnected(!!status.connected);
        setMsAccountEmail(status.ms_account_email || "");
        setMsAccountName(status.ms_display_name || "");
        setMsCheckDone(true);
      })
      .catch(() => setMsCheckDone(true));
  }, []);

  const initExplorer = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ddFolder, projectsData] = await Promise.all([
        getDdRoot(),
        getProjects(),
      ]);
      setDdRootId(ddFolder.id);
      setProjects(projectsData);
      const children = await listFolder(ddFolder.id);

      // Check for a pending deep-link from the Projects page
      const pendingFolder = takePendingFolderRequest();
      let rootCrumbs;
      if (pendingFolder) {
        const folderChildren = await listFolder(pendingFolder.id);
        setItems(folderChildren);
        rootCrumbs = [
          { id: ddFolder.id, name: DD_FOLDER_NAME },
          { id: pendingFolder.id, name: pendingFolder.name },
        ];
        setBreadcrumbs(rootCrumbs);
      } else {
        setItems(children);
        rootCrumbs = [{ id: ddFolder.id, name: DD_FOLDER_NAME }];
        setBreadcrumbs(rootCrumbs);
      }

      // Seed the browser history entry so back-navigation can restore to root.
      selfNavigate.current = true;
      navigate("/files", {
        state: { ...location.state, fileCrumbs: rootCrumbs },
        replace: true,
      });
      initialized.current = true;
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to connect to OneDrive.");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (msConnected && msCheckDone) {
      initExplorer();
    }
  }, [msConnected, msCheckDone, initExplorer]);

  // ── Browser back / forward navigation ─────────────────────────────────────
  // location.key changes on every history transition (back, forward, push).
  // We skip the event when WE triggered the navigate() call (selfNavigate ref).
  const locationKeyRef = useRef(location.key); // track initial key to skip mount
  useEffect(() => {
    // Skip the very first render — initExplorer handles it.
    if (locationKeyRef.current === location.key) return;
    locationKeyRef.current = location.key;

    if (!initialized.current || !msConnected) return;
    if (selfNavigate.current) { selfNavigate.current = false; return; }

    const crumbs = location.state?.fileCrumbs;
    if (!crumbs || crumbs.length === 0) {
      // Back to /files with no folder state → reinitialise to root.
      initialized.current = false;
      initExplorer();
      return;
    }
    const targetId = crumbs[crumbs.length - 1].id;
    setBreadcrumbs(crumbs);
    loadFolder(targetId);
  // location.key is the only dep we want — everything else is stable via refs/callbacks.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // ── Previous-versions lookup ───────────────────────────────────────────────
  // Whenever the file list changes, ask the backend which of the visible
  // files have an archived previous version. The endpoint returns ONLY
  // drawings that actually have at least one archived file — so we don't
  // render the button for drawings without history.
  useEffect(() => {
    const fileIds = (items || []).filter((i) => !i.folder).map((i) => i.id);
    if (fileIds.length === 0) {
      setPreviousVersions({});
      return;
    }
    let cancelled = false;
    checkPreviousVersions(fileIds)
      .then((result) => { if (!cancelled) setPreviousVersions(result || {}); })
      .catch(() => { if (!cancelled) setPreviousVersions({}); });
    return () => { cancelled = true; };
  }, [items]);

  // ── Archive folder context ─────────────────────────────────────────────────
  // When the user navigates into ANY folder, ask the backend whether this
  // folder is a tracked "previous versions/<drawing>" archive folder. If yes
  // we render a "Restore this version" button on every file inside.
  useEffect(() => {
    const folderId = breadcrumbs.length ? breadcrumbs[breadcrumbs.length - 1].id : null;
    if (!folderId) { setArchiveContext(null); return; }
    let cancelled = false;
    getArchiveFolderContext(folderId)
      .then((ctx) => {
        if (cancelled) return;
        setArchiveContext(ctx && ctx.is_archive_folder ? ctx : null);
      })
      .catch(() => { if (!cancelled) setArchiveContext(null); });
    return () => { cancelled = true; };
  }, [breadcrumbs]);

  // ── Navigation ─────────────────────────────────────────────────────────────

  const toggleViewMode = useCallback((mode) => {
    setViewMode(mode);
    localStorage.setItem("exploreViewMode", mode);
  }, []);

  const loadFolder = useCallback(async (folderId) => {
    setLoading(true);
    setError(null);
    setConfirmDeleteId(null);
    setRenamingId(null);
    try {
      const children = await listFolder(folderId);
      setItems(children);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const navigateInto = (folder) => {
    const next = [...breadcrumbs, { id: folder.id, name: folder.name }];
    setBreadcrumbs(next);
    loadFolder(folder.id);
    selfNavigate.current = true;
    navigate("/files", {
      state: { ...location.state, fileCrumbs: next },
      replace: false,
    });
  };

  /**
   * Open the archive folder for a drawing (previous-versions folder).
   * Uses the same navigation flow as clicking a folder — pushes a new
   * breadcrumb so the user can use the back arrow.
   */
  const openPreviousVersions = (drawing) => {
    const info = previousVersions[drawing.id];
    if (!info?.archive_folder_id) return;
    const archiveName = `${drawing.name} — Previous Versions`;
    const next = [...breadcrumbs, { id: info.archive_folder_id, name: archiveName }];
    setBreadcrumbs(next);
    loadFolder(info.archive_folder_id);
    selfNavigate.current = true;
    navigate("/files", {
      state: { ...location.state, fileCrumbs: next },
      replace: false,
    });
  };

  /**
   * Restore an archived file as the current version of its drawing.
   * The current drawing is archived (with name-collision suffix) and the
   * archived file is moved to drawings/.
   */
  const restoreArchivedVersion = async (archivedItem) => {
    if (!archiveContext) return;
    const targetName = archiveContext.current_drawing_name || "the current drawing";
    const ok = window.confirm(
      `Restore "${archivedItem.name}" as the current drawing?\n\n` +
      `"${targetName}" will be moved into this archive folder so you can recover it later.`
    );
    if (!ok) return;
    setRestoringId(archivedItem.id);
    try {
      await restoreDrawingVersion(archivedItem.id);
      toast.success(`Restored "${archivedItem.name}" as the current drawing.`);
      // Reload this folder so the row order reflects the swap.
      const folderId = breadcrumbs[breadcrumbs.length - 1].id;
      await loadFolder(folderId);
    } catch (e) {
      toast.error(`Restore failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRestoringId(null);
    }
  };

  const navigateToBreadcrumb = (index) => {
    if (index === breadcrumbs.length - 1) return; // already here
    const next = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(next);
    loadFolder(next[next.length - 1].id);
    selfNavigate.current = true;
    navigate("/files", {
      state: { ...location.state, fileCrumbs: next },
      replace: false,
    });
  };

  // ── Sign in / out ──────────────────────────────────────────────────────────

  const handleGrantAccess = async () => {
    setMsConnecting(true);
    try {
      const authUrl = await getMsAuthorizeUrl();
      window.location.href = authUrl;
    } catch (e) {
      toast.error("Could not start Microsoft authorization.");
      setMsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectMs();
      setMsConnected(false);
      setItems([]); setBreadcrumbs([]); setDdRootId(null);
      toast.success("Microsoft account disconnected.");
    } catch (e) {
      toast.error("Could not disconnect.");
    }
  };

  // ── Rename ─────────────────────────────────────────────────────────────────

  const startRename = (item) => {
    setRenamingId(item.id);
    setRenameValue(item.name);
  };

  const cancelRename = () => setRenamingId(null);

  const commitRename = useCallback(async (item) => {
    if (renamingId !== item.id) return;
    const newName = renameValue.trim();
    setRenamingId(null);
    if (!newName || newName === item.name) return;

    try {
      const updated = await renameItem(item.id, newName);
      setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, name: newName, webUrl: updated.webUrl } : i));

      // Sync DB if this is a project folder (depth 0, is a folder)
      if (depth === 0 && item.folder) {
        const match = projects.find((p) => p.onedrive_folder_id === item.id);
        if (match) {
          const saved = await updateProject(match.id, {
            onedrive_folder_name: newName,
            onedrive_folder_url: updated.webUrl || match.onedrive_folder_url,
          });
          setProjects((prev) => prev.map((p) => p.id === saved.id ? saved : p));
          toast.success(`Folder renamed and project record updated.`);
          return;
        }
      }
      toast.success(`Renamed to "${newName}".`);
    } catch (e) {
      toast.error(`Rename failed: ${e.response?.data?.detail || e.message}`);
    }
  }, [renamingId, renameValue, depth, projects]);

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDelete = useCallback(async (item) => {
    setConfirmDeleteId(null);
    setDeletingId(item.id);
    try {
      await deleteItem(item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success(`"${item.name}" deleted.`);
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDeletingId(null);
    }
  }, []);

  // ── Move ───────────────────────────────────────────────────────────────────

  const handleMove = useCallback(async (destinationFolderId) => {
    if (!movingItem) return;
    const item = movingItem;
    setMovingItem(null);
    try {
      await moveItem(item.id, destinationFolderId);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success(`"${item.name}" moved.`);
    } catch (e) {
      toast.error(`Move failed: ${e.response?.data?.detail || e.message}`);
    }
  }, [movingItem]);

  // ── Upload ─────────────────────────────────────────────────────────────────

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentFolderId) return;
    setUploading(true);
    try {
      const uploaded = await uploadFile(currentFolderId, file, currentProject?.id || null);
      setItems((prev) => {
        const withoutOld = prev.filter((i) => i.name !== uploaded.name);
        return [
          ...withoutOld.filter((i) => i.folder),
          ...withoutOld.filter((i) => !i.folder && i.name < uploaded.name),
          { ...uploaded },
          ...withoutOld.filter((i) => !i.folder && i.name >= uploaded.name),
        ];
      });
      toast.success(`"${file.name}" uploaded.`);
    } catch (e) {
      toast.error(`Upload failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ── Ask About Drawing → load into Open Analysis ────────────────────────────

  const handleAskAboutDrawing = useCallback(async (item) => {
    setAnalyzingId(item.id);
    let fileReady = null;
    try {
      const resp = await downloadFileItem(item.id);
      // resp.data is already a Blob when responseType:"blob" — create File directly
      const mimeType = resp.data?.type || resp.headers["content-type"] || "application/octet-stream";
      fileReady = new File([resp.data], item.name, { type: mimeType });
    } catch (e) {
      console.error("Ask drawing error:", e);
      // Parse blob error bodies to surface the real backend message
      let msg = e.message;
      if (e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const json = JSON.parse(txt);
          msg = json.detail || json.message || msg;
        } catch (_) {
          // Fall back to the original network error when the blob is not JSON.
        }
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(`Could not load drawing: ${msg}`);
    } finally {
      setAnalyzingId(null);
    }
    // Navigate outside the try block so a routing error doesn't mask the real download error
    if (fileReady) {
      setPendingAnalysisFile(fileReady);
      navigate("/drawing-evaluation", { state: { initialMode: "analysis" } });
    }
  }, [navigate]);

  // ── Compare to Previous Version ────────────────────────────────────────────

  const handleCompareToPrevoius = useCallback(async (item) => {
    if (!currentProject) {
      toast.error("Cannot determine project for this drawing.");
      return;
    }
    setComparingId(item.id);
    try {
      const res = await compareFromVersionHistory(item.id, currentProject.id);
      const { session_id } = res.data;
      toast.success("Comparison session created. Opening comparison tool…");
      navigate("/drawing-evaluation", {
        state: { initialMode: "compare", initialCompareId: session_id },
      });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Compare failed: ${msg}`);
    } finally {
      setComparingId(null);
    }
  }, [currentProject, navigate]);

  // ── Open (new tab) ─────────────────────────────────────────────────────────

  const openItem = (item) => {
    if (!item.webUrl) { toast.error("No URL available for this item."); return; }
    window.open(item.webUrl, "_blank", "noopener,noreferrer");
  };

  /**
   * Called on double-click for any file (non-folder) item.
   * - .html files inside an "emails" folder → native slide-over preview
   * - everything else → open in OneDrive (new tab)
   */
  const handleFileOpen = useCallback(async (item) => {
    const isEmailHtml =
      item.name?.toLowerCase().endsWith(".html") &&
      parentName?.toLowerCase() === "emails";

    if (!isEmailHtml) {
      openItem(item);
      return;
    }

    // Native email preview
    setEmailPreview({ open: true, html: "", filename: item.name, loading: true });
    try {
      const { html } = await getEmailFileContent(item.id);
      setEmailPreview({ open: true, html, filename: item.name, loading: false });
    } catch {
      toast.error("Could not load email preview.");
      setEmailPreview({ open: false, html: "", filename: "", loading: false });
    }
  }, [parentName]);

  const closeEmailPreview = () =>
    setEmailPreview({ open: false, html: "", filename: "", loading: false });

  // ── AI Chat ────────────────────────────────────────────────────────────────

  // Auto-scroll chat to bottom whenever messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, chatLoading]);

  // Enrich items with computed depth/badge for the AI context
  const enrichedItems = useMemo(() => items.map((item) => {
    const perms = permissions(item, depth, parentName, completedOffset);
    return { ...item, _depth: depth, _parentName: parentName, _badge: perms.badge };
  }), [items, depth, parentName, completedOffset]);

  // Meeting recordings that have been moved into the folder currently being browsed.
  // Guarded against out-of-order responses: if the user navigates to another
  // folder before an in-flight request resolves, that stale response is
  // discarded instead of overwriting the (now-current) folder's recordings.
  const folderRecordingsRequestId = useRef(0);
  useEffect(() => {
    const requestId = ++folderRecordingsRequestId.current;
    if (!currentProject || !currentFolderId) {
      setFolderRecordings([]);
      return;
    }
    getRecordingsByFolder(currentProject.id, currentFolderId)
      .then((data) => {
        if (folderRecordingsRequestId.current === requestId) setFolderRecordings(data || []);
      })
      .catch(() => {
        if (folderRecordingsRequestId.current === requestId) setFolderRecordings([]);
      });
  }, [currentProject, currentFolderId]);

  // Hide contractor folders that are marked "not_needed" when inside "3. Transmittal"
  const displayItems = useMemo(() => {
    if (parentName?.toLowerCase() !== "3. transmittal") return items;
    if (!currentProject) return items;

    const notNeededTypes = new Set(
      (currentProject.contractors || [])
        .filter((c) => c.not_needed)
        .map((c) => c.type)
    );
    if (notNeededTypes.size === 0) return items;

    return items.filter((item) => {
      if (!item.folder) return true;
      const m = item.name?.match(/^2\.\d+\s+(.+)$/);
      if (!m) return true;
      return !notNeededTypes.has(m[1]);
    });
  }, [items, parentName, currentProject]);

  const handleSendChat = useCallback(async () => {
    const msg = chatInput.trim();
    if (!msg || chatLoading) return;
    setChatInput("");
    const userMsg = { id: Date.now().toString(), role: "user", content: msg };
    setChatMessages((prev) => [...prev, userMsg]);
    setChatLoading(true);
    try {
      const currentPath = breadcrumbs.map((b) => b.name);
      const result = await sendFileChat(
        chatSessionId, msg, enrichedItems, currentPath,
        currentFolderId,
        currentProject?.id || null,
      );
      const assistantMsg = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: result.reply,
        actions: result.actions || [],
        results: result.results || [],
        retrievalStatus: result.retrieval_status || null,
      };
      setChatMessages((prev) => [...prev, assistantMsg]);
    } catch (e) {
      const errMsg = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: `Sorry, I encountered an error: ${e.response?.data?.detail || e.message}`,
        actions: [],
        isError: true,
      };
      setChatMessages((prev) => [...prev, errMsg]);
    } finally {
      setChatLoading(false);
    }
  }, [chatInput, chatLoading, chatSessionId, enrichedItems, breadcrumbs, currentFolderId, currentProject]);

  const handleBrowseAssistantResult = useCallback((target) => {
    const targetCrumbs = Array.isArray(target?.breadcrumbs) ? target.breadcrumbs : [];
    if (!target?.scope_root_id || targetCrumbs.length === 0) return;
    const rootIndex = breadcrumbs.findIndex((crumb) => crumb.id === target.scope_root_id);
    if (rootIndex < 0) {
      toast.error("That folder is outside the current Explore Files path. Open it in OneDrive instead.");
      return;
    }
    const validCrumbs = targetCrumbs.filter((crumb) => crumb?.id && crumb?.name);
    if (validCrumbs.length === 0) return;
    const next = [...breadcrumbs.slice(0, rootIndex), ...validCrumbs];
    setBreadcrumbs(next);
    loadFolder(next[next.length - 1].id);
    selfNavigate.current = true;
    navigate("/files", {
      state: { ...location.state, fileCrumbs: next },
      replace: false,
    });
  }, [breadcrumbs, loadFolder, location.state, navigate]);

  const handleClearChat = useCallback(async () => {
    setChatMessages([]);
    try {
      await clearFileChat(chatSessionId);
    } catch (_) {
      // Clearing local messages still succeeds when the remote session is already gone.
    }
  }, [chatSessionId]);

  // Execute confirmed delete from AI suggestion
  const handleAIConfirmDelete = useCallback(async () => {
    if (!pendingDeleteAction) return;
    const action = pendingDeleteAction;
    setPendingDeleteAction(null);
    setDeletingId(action.item_id);
    try {
      await deleteItem(action.item_id);
      setItems((prev) => prev.filter((i) => i.id !== action.item_id));
      toast.success(`"${action.item_name}" deleted.`);
      setChatMessages((prev) => [...prev, {
        id: Date.now().toString(), role: "assistant",
        content: `Done! I've deleted **"${action.item_name}"** from OneDrive.`, actions: [],
      }]);
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
      setChatMessages((prev) => [...prev, {
        id: Date.now().toString(), role: "assistant",
        content: `Delete failed: ${e.response?.data?.detail || e.message}`, actions: [], isError: true,
      }]);
    } finally {
      setDeletingId(null);
    }
  }, [pendingDeleteAction]);

  // Execute confirmed move from AI suggestion
  const handleAIMoveConfirm = useCallback(async (destinationFolderId) => {
    if (!pendingMoveAction) return;
    const action = pendingMoveAction;
    setPendingMoveAction(null);
    try {
      await moveItem(action.item_id, destinationFolderId);
      setItems((prev) => prev.filter((i) => i.id !== action.item_id));
      toast.success(`"${action.item_name}" moved.`);
      setChatMessages((prev) => [...prev, {
        id: Date.now().toString(), role: "assistant",
        content: `Done! I've moved **"${action.item_name}"** to the selected folder.`, actions: [],
      }]);
    } catch (e) {
      toast.error(`Move failed: ${e.response?.data?.detail || e.message}`);
      setChatMessages((prev) => [...prev, {
        id: Date.now().toString(), role: "assistant",
        content: `Move failed: ${e.response?.data?.detail || e.message}`, actions: [], isError: true,
      }]);
    }
  }, [pendingMoveAction]);

  // Execute a direct AI move (when AI provided a destination_folder_id)
  const handleAIDirectMove = useCallback(async () => {
    if (!pendingMoveAction?.destination_folder_id) return;
    await handleAIMoveConfirm(pendingMoveAction.destination_folder_id);
  }, [pendingMoveAction, handleAIMoveConfirm]);

  // ── Drag-and-drop within the file explorer ─────────────────────────────────

  const handleDragDrop = useCallback(async (targetFolder) => {
    if (!draggingItem) return;
    const item = draggingItem;
    setDraggingItem(null);
    setDragOverId(null);
    // Can't drop a folder onto itself
    if (item.id === targetFolder.id) return;
    try {
      await moveItem(item.id, targetFolder.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success(`"${item.name}" moved to "${targetFolder.name}".`);
    } catch (e) {
      toast.error(`Move failed: ${e.response?.data?.detail || e.message}`);
    }
  }, [draggingItem]);

  // ── Manual "New Folder" creation ───────────────────────────────────────────

  const handleCreateFolder = useCallback(async () => {
    const name = newFolderName.trim();
    if (!name || !currentFolderId) return;
    setNewFolderLoading(true);
    try {
      const newFolder = await createFolder(currentFolderId, name);
      setItems((prev) => [newFolder, ...prev]);
      toast.success(`Folder "${name}" created.`);
      setCreatingFolder(false);
      setNewFolderName("");
    } catch (e) {
      toast.error(`Create failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setNewFolderLoading(false);
    }
  }, [newFolderName, currentFolderId]);

  const cancelCreateFolder = useCallback(() => {
    setCreatingFolder(false);
    setNewFolderName("");
  }, []);

  // ── AI-suggested folder creation ───────────────────────────────────────────

  const handleAIConfirmCreateFolder = useCallback(async (action) => {
    setPendingCreateFolderAction(null);
    const parentId = (action.parent_folder_id === "current" || !action.parent_folder_id)
      ? currentFolderId
      : action.parent_folder_id;
    if (!parentId) {
      toast.error("Cannot create folder — no folder is currently open.");
      return;
    }
    try {
      const newFolder = await createFolder(parentId, action.folder_name);
      setItems((prev) => [newFolder, ...prev]);
      toast.success(`Folder "${action.folder_name}" created.`);
      setChatMessages((prev) => [...prev, {
        id: Date.now().toString(), role: "assistant",
        content: `Done! Created folder **"${action.folder_name}"**.`, actions: [],
      }]);
    } catch (e) {
      toast.error(`Create failed: ${e.response?.data?.detail || e.message}`);
      setChatMessages((prev) => [...prev, {
        id: Date.now().toString(), role: "assistant",
        content: `Folder creation failed: ${e.response?.data?.detail || e.message}`, actions: [], isError: true,
      }]);
    }
  }, [currentFolderId]);

  // ── AI-suggested layout modification (requires user approval) ──────────────

  const handleAIModifyLayout = useCallback(async (action) => {
    const projectId = action.project_id || currentProject?.id;
    if (!projectId) {
      toast.error("Cannot modify layout — project context is missing.");
      return;
    }
    try {
      const result = await applyLayoutModification(projectId, action.modification, action.changes);
      toast.success(`Layout updated: ${result.applied.join(", ")}`);
      setChatMessages((prev) => [...prev, {
        id: Date.now().toString(), role: "assistant",
        content: `Done! Applied layout changes:\n${result.applied.map((a) => `• ${a}`).join("\n")}`, actions: [],
      }]);
    } catch (e) {
      toast.error(`Layout modification failed: ${e.response?.data?.detail || e.message}`);
      setChatMessages((prev) => [...prev, {
        id: Date.now().toString(), role: "assistant",
        content: `Layout modification failed: ${e.response?.data?.detail || e.message}`, actions: [], isError: true,
      }]);
    }
  }, [currentProject]);

  // ── AI-suggested add contractor ────────────────────────────────────────────

  const handleAIConfirmAddContractor = useCallback(async (action, contactName = "", contactEmail = "") => {
    setPendingAddContractorAction(null);
    setContractorContactModal(null);
    const projectId = action.project_id || currentProject?.id;
    if (!projectId) {
      toast.error("Cannot add contractor — project context is missing. Navigate into a project folder first.");
      return;
    }
    try {
      const updatedProject = await enableContractor(
        projectId, action.contractor_name,
        contactName.trim() || null, contactEmail.trim() || null,
      );
      // Refresh projects list so the new contractor folder becomes visible
      setProjects((prev) => prev.map((p) => p.id === updatedProject.id ? updatedProject : p));
      const contactNote = contactName || contactEmail
        ? ` Contact saved: ${[contactName, contactEmail].filter(Boolean).join(" / ")}.`
        : "";
      toast.success(`Contractor "${action.contractor_name}" enabled.${contactNote}`);
      // Reload the current folder so the new folder appears (e.g. when inside Transmittal)
      if (currentFolderId) loadFolder(currentFolderId);
      setChatMessages((prev) => [...prev, {
        id: Date.now().toString(), role: "assistant",
        content: `Done! **"${action.contractor_name}"** contractor folder is now active and visible in the Transmittal section.${contactNote}`, actions: [],
      }]);
    } catch (e) {
      toast.error(`Failed to add contractor: ${e.response?.data?.detail || e.message}`);
      setChatMessages((prev) => [...prev, {
        id: Date.now().toString(), role: "assistant",
        content: `Failed to add contractor: ${e.response?.data?.detail || e.message}`, actions: [], isError: true,
      }]);
    }
  }, [currentProject, currentFolderId, loadFolder]);

  // ── Render: not connected ──────────────────────────────────────────────────

  if (!msCheckDone) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <ArrowsClockwise size={26} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (!msConnected) {
    return <ConnectScreen onGrantAccess={handleGrantAccess} isConnecting={msConnecting} />;
  }

  // ── Page-level drag & drop: intercept file drops anywhere on the page ──────
  // Drops open the Smart Upload modal and pre-seed the dropped files.
  const handlePageDragOver = (e) => {
    // Only intercept drops that carry files (not internal DOM drag)
    if (e.dataTransfer.types.includes("Files")) {
      e.preventDefault();
      setPageIsDragging(true);
    }
  };

  const handlePageDragLeave = (e) => {
    // Only clear when leaving the outermost container
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setPageIsDragging(false);
    }
  };

  const handlePageDrop = (e) => {
    e.preventDefault();
    setPageIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    if (!droppedFiles.length) return;
    setSmartUploadFiles(droppedFiles);
    setSmartUploadProject(currentProject);
    setSmartUploadOpen(true);
  };

  // ── Render: explorer ──────────────────────────────────────────────────────

  return (
    <div
      className="flex flex-1 overflow-hidden relative"
      data-testid="explore-files-page"
      onDragOver={handlePageDragOver}
      onDragLeave={handlePageDragLeave}
      onDrop={handlePageDrop}
    >
      {/* Page-level drag overlay */}
      {pageIsDragging && (
        <div
          className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none"
          data-testid="page-drag-overlay"
        >
          <div className="absolute inset-4 rounded-lg border-2 border-dashed border-violet-400 bg-violet-50/80 backdrop-blur-sm" />
          <div className="relative z-10 flex flex-col items-center gap-3 text-violet-700">
            <Sparkle size={36} weight="fill" className="text-violet-500" />
            <p className="text-lg font-bold tracking-tight">Drop to Smart Upload</p>
            <p className="text-sm text-violet-500">
              {currentProject ? `→ ${currentProject.name}` : "Select project after drop"}
            </p>
          </div>
        </div>
      )}

      {/* ── Main file explorer column ───────────────────────────────────── */}
      <div className="flex flex-col flex-1 overflow-hidden min-w-0 bg-slate-50">

      {/* Page Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-4 flex-shrink-0">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-shrink-0">
            <h1 className="font-cabinet text-2xl font-black tracking-tight text-slate-900">Explore Files</h1>
            <p className="text-sm text-slate-500 mt-0.5 truncate">
              Browsing your OneDrive — {DD_FOLDER_NAME}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
            {/* User badge — shows the connected Microsoft account email when available */}
            <div
              className="hidden sm:flex items-center gap-2 bg-slate-100 border border-slate-200 rounded px-2.5 py-1.5"
              title={msAccountEmail ? `Microsoft account: ${msAccountEmail}` : undefined}
              data-testid="header-user-badge"
            >
              <div className="w-5 h-5 rounded-full bg-[#0078D4] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-[9px] font-bold">
                  {(msAccountEmail || user?.email)?.charAt(0)?.toUpperCase() || "M"}
                </span>
              </div>
              <div className="flex flex-col leading-none">
                {msAccountName && (
                  <span className="text-[10px] font-semibold text-slate-800 max-w-[160px] truncate">
                    {msAccountName}
                  </span>
                )}
                <span className="text-[10px] text-slate-500 max-w-[160px] truncate">
                  {msAccountEmail || user?.email}
                </span>
              </div>
            </div>

            {/* AI Chat toggle */}
            <Button
              variant={chatOpen ? "default" : "outline"}
              size="sm"
              onClick={() => setChatOpen((v) => !v)}
              className={chatOpen ? "bg-violet-600 hover:bg-violet-700 text-white border-violet-600" : "text-slate-700 border-slate-200"}
              data-testid="ai-chat-toggle-btn"
              title="AI File Assistant"
            >
              <Robot size={13} className="mr-1.5" />
              AI Assistant
            </Button>

            <Button
              size="sm"
              onClick={() => { setSmartUploadFiles([]); setSmartUploadProject(currentProject); setSmartUploadOpen(true); }}
              disabled={projects.length === 0}
              className="bg-violet-600 hover:bg-violet-700 text-white border-violet-600"
              data-testid="smart-upload-btn"
              title="Smart Upload — AI sorts files into the correct project subfolders"
            >
              <Sparkle size={13} className="mr-1.5" weight="fill" />
              Smart Upload
            </Button>

            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
              disabled={uploading || !currentFolderId} className="text-slate-700" data-testid="upload-btn">
              {uploading ? <ArrowsClockwise size={13} className="mr-1.5 animate-spin" /> : <CloudArrowUp size={13} className="mr-1.5" />}
              {uploading ? "Uploading…" : "Upload"}
            </Button>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} data-testid="file-input" />

            <Button
              variant="outline" size="sm"
              onClick={() => { setCreatingFolder(true); setNewFolderName(""); }}
              disabled={!currentFolderId}
              className="text-slate-700"
              data-testid="new-folder-btn"
              title="Create a new folder in the current location"
            >
              <FolderPlus size={13} className="mr-1.5" />
              New Folder
            </Button>

            <Button variant="outline" size="sm" onClick={() => currentFolderId && loadFolder(currentFolderId)}
              disabled={loading} className="text-slate-700 px-2" data-testid="refresh-btn">
              <ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} />
            </Button>

            <Button variant="outline" size="sm" onClick={handleDisconnect}
              className="text-red-600 border-red-200 hover:bg-red-50" data-testid="sign-out-btn">
              Disconnect
            </Button>
          </div>
        </div>
      </div>

      {/* Breadcrumb bar */}
      <div className="bg-white border-b border-slate-100 px-4 py-2 flex items-center justify-between gap-1 flex-shrink-0" data-testid="breadcrumb-bar">
        <div className="flex items-center gap-1 overflow-x-auto min-w-0">
        {/* Back button — visible when there is history to go back to */}
        <button
          onClick={() => navigate(-1)}
          data-testid="back-btn"
          title="Go back"
          className="flex items-center justify-center w-6 h-6 rounded mr-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors flex-shrink-0 disabled:opacity-30 disabled:pointer-events-none"
        >
          <ArrowLeft size={14} weight="bold" />
        </button>

        {breadcrumbs.map((crumb, i) => {
          const isLast = i === breadcrumbs.length - 1;
          return (
            <span key={crumb.id} className="flex items-center gap-1 flex-shrink-0">
              {i > 0 && <CaretRight size={11} className="text-slate-300 mx-0.5" />}
              <button
                onClick={() => navigateToBreadcrumb(i)}
                disabled={isLast}
                className={`flex items-center gap-1 text-xs px-1 py-0.5 rounded transition-colors ${
                  isLast
                    ? "font-semibold text-slate-800 cursor-default"
                    : "text-slate-500 hover:text-primary hover:bg-primary/5"
                }`}
                data-testid={`breadcrumb-${crumb.name}`}
              >
                {i === 0 ? <FolderOpen size={12} className="text-[#0078D4]" /> : <FolderSimple size={12} className="text-slate-400" />}
                {crumb.name}
              </button>
            </span>
          );
        })}
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-0.5 bg-slate-100 rounded p-0.5 flex-shrink-0 ml-2" data-testid="view-toggle">
          <button
            onClick={() => toggleViewMode("list")}
            title="List view"
            data-testid="view-list-btn"
            className={`flex items-center justify-center w-6 h-6 rounded transition-colors ${viewMode === "list" ? "bg-white shadow-sm text-slate-700" : "text-slate-400 hover:text-slate-600"}`}
          >
            <Rows size={13} />
          </button>
          <button
            onClick={() => toggleViewMode("grid")}
            title="Grid view"
            data-testid="view-grid-btn"
            className={`flex items-center justify-center w-6 h-6 rounded transition-colors ${viewMode === "grid" ? "bg-white shadow-sm text-slate-700" : "text-slate-400 hover:text-slate-600"}`}
          >
            <SquaresFour size={13} />
          </button>
        </div>
      </div>

      {/* Inline New Folder creation bar */}
      {creatingFolder && (
        <div className="bg-amber-50 border-b border-amber-200 px-8 py-2.5 flex items-center gap-3 flex-shrink-0" data-testid="new-folder-bar">
          <FolderPlus size={16} className="text-amber-600 flex-shrink-0" />
          <input
            autoFocus
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); handleCreateFolder(); }
              if (e.key === "Escape") cancelCreateFolder();
            }}
            placeholder="New folder name…"
            className="flex-1 text-sm border border-amber-300 rounded-sm px-2.5 py-1 focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400 bg-white"
            data-testid="new-folder-input"
          />
          <button
            onClick={handleCreateFolder}
            disabled={!newFolderName.trim() || newFolderLoading}
            className="text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-40 px-3 py-1.5 rounded transition-colors"
            data-testid="new-folder-create-btn"
          >
            {newFolderLoading ? <ArrowsClockwise size={13} className="animate-spin" /> : "Create"}
          </button>
          <button
            onClick={cancelCreateFolder}
            className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1.5"
            data-testid="new-folder-cancel-btn"
          >
            Cancel
          </button>
        </div>
      )}

      {/* File area — list or grid */}
      <div className="flex-1 overflow-y-auto px-8 py-4">
        {folderRecordings.length > 0 && (
          <div className="mb-4 bg-violet-50/50 border border-violet-200 rounded-sm overflow-hidden" data-testid="folder-recordings-section">
            <div className="px-3 py-2 border-b border-violet-100 flex items-center gap-1.5">
              <Microphone size={13} weight="fill" className="text-violet-500" />
              <span className="text-[11px] font-semibold uppercase tracking-wide text-violet-600">Meeting Recordings</span>
            </div>
            <div className="divide-y divide-violet-100">
              {folderRecordings.map((r) => (
                <button
                  key={r.id}
                  onClick={() => navigate(`/recordings/${r.id}`)}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-violet-100/60 transition-colors text-left"
                  data-testid={`folder-recording-item-${r.id}`}
                >
                  <Microphone size={15} weight="fill" className="text-violet-500 flex-shrink-0" />
                  <span className="flex-1 text-sm text-slate-700 truncate">
                    Meeting Recording &middot; {fmtDate(r.created_at)}
                  </span>
                  {r.status === "completed" ? (
                    <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full uppercase">
                      <CheckCircle size={9} weight="fill" /> Completed
                    </span>
                  ) : (
                    <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full uppercase">
                      {r.status}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
        {error && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 p-3 mb-4 rounded-sm" data-testid="error-banner">
            <Warning size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700 flex-1">{error}</p>
            <button className="text-xs text-red-400 hover:underline" onClick={() => currentFolderId && loadFolder(currentFolderId)}>Retry</button>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20" data-testid="loading-state">
            <ArrowsClockwise size={26} className="text-[#0078D4] animate-spin" />
            <p className="text-sm text-slate-500">Loading…</p>
          </div>
        ) : viewMode === "grid" ? (

          /* ── GRID VIEW ────────────────────────────────────────────────────── */
          <div className="bg-white border border-slate-200" data-testid="files-grid">
            {displayItems.length === 0 ? (
              <div className="py-16 text-center" data-testid="empty-folder-state">
                <FolderOpen size={32} className="text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-500">This folder is empty</p>
                <p className="text-xs text-slate-400 mt-1">Upload a file to get started.</p>
              </div>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-0.5 p-3">
                {displayItems.map((item) => {
                  const { Icon, cls } = fileIcon(item);
                  const perms = permissions(item, depth, parentName, completedOffset);
                  const isRenaming = renamingId === item.id;
                  const isDeleting = deletingId === item.id;
                  const isConfirmingDelete = confirmDeleteId === item.id;
                  const isAnalyzing = analyzingId === item.id;
                  const isRestoring = restoringId === item.id;
                  const iconSize = item.folder ? 44 : 36;

                  return (
                    <div
                      key={item.id}
                      className={`relative group flex flex-col items-center gap-1.5 p-3 rounded-lg hover:bg-slate-100 transition-colors select-none
                        ${draggingItem && item.folder && item.id !== draggingItem?.id && dragOverId === item.id ? "bg-violet-50 ring-2 ring-inset ring-violet-300" : ""}
                        ${draggingItem && item.id === draggingItem?.id ? "opacity-40" : ""}
                      `}
                      data-testid={`item-grid-${item.name}`}
                      draggable={perms.canMove}
                      onDragStart={(e) => { e.stopPropagation(); setDraggingItem(item); e.dataTransfer.effectAllowed = "move"; }}
                      onDragEnd={() => { setDraggingItem(null); setDragOverId(null); }}
                      onDragOver={(e) => { if (draggingItem && item.folder && item.id !== draggingItem?.id) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverId(item.id); } }}
                      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverId(null); }}
                      onDrop={(e) => { e.preventDefault(); if (draggingItem && item.folder && item.id !== draggingItem?.id) handleDragDrop(item); setDragOverId(null); }}
                    >
                      {/* Delete confirm overlay */}
                      {isConfirmingDelete && (
                        <div className="absolute inset-0 rounded-lg bg-white/96 border border-red-200 flex flex-col items-center justify-center gap-2 z-10 p-2">
                          <span className="text-[10px] font-semibold text-red-700 text-center leading-tight">Delete “{item.name.length > 18 ? item.name.slice(0, 18) + "…" : item.name}”?</span>
                          <div className="flex items-center gap-2">
                            <button onClick={() => handleDelete(item)} className="text-[11px] font-bold text-white bg-red-500 hover:bg-red-600 px-2.5 py-0.5 rounded transition-colors" data-testid={`grid-confirm-delete-${item.name}`}>Delete</button>
                            <button onClick={() => setConfirmDeleteId(null)} className="text-[11px] text-slate-500 hover:underline">Cancel</button>
                          </div>
                        </div>
                      )}

                      {/* Icon */}
                      <div
                        className="relative"
                        onClick={item.folder ? () => navigateInto(item) : undefined}
                        onDoubleClick={!item.folder ? () => handleFileOpen(item) : undefined}
                      >
                        {isDeleting || isAnalyzing || isRestoring ? (
                          <ArrowsClockwise size={iconSize} className="text-slate-300 animate-spin" />
                        ) : (
                          <Icon size={iconSize} className={`${cls} ${item.folder ? "cursor-pointer" : ""}`} />
                        )}
                        {/* Protection dot */}
                        {perms.badge === "project"   && <span title="Project folder" className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-primary ring-1 ring-white" />}
                        {perms.badge === "protected" && <span title="Protected"      className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-slate-400 ring-1 ring-white" />}
                        {perms.badge === "system"    && <span title="System folder"  className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-1 ring-white" />}
                      </div>

                      {/* Name */}
                      {isRenaming ? (
                        <input
                          autoFocus
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); commitRename(item); }
                            if (e.key === "Escape") cancelRename();
                          }}
                          onBlur={() => commitRename(item)}
                          className="w-full text-[11px] text-center border border-primary rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary/30 bg-white"
                          data-testid={`rename-input-grid-${item.name}`}
                        />
                      ) : (
                        <span
                          className="text-[11px] text-slate-700 text-center leading-tight line-clamp-2 w-full break-words"
                          title={item.name}
                          onClick={item.folder ? () => navigateInto(item) : undefined}
                          onDoubleClick={!item.folder ? () => handleFileOpen(item) : undefined}
                          style={{ cursor: item.folder ? "pointer" : "default" }}
                        >
                          {item.name}
                        </span>
                      )}

                      {/* Hover action bar — top-right corner */}
                      {!isRenaming && !isConfirmingDelete && !isDeleting && (
                        <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          {isAnalyzable(item) && (
                            <button
                              title="Ask about this drawing"
                              onClick={() => handleAskAboutDrawing(item)}
                              disabled={!!analyzingId}
                              className="w-5 h-5 flex items-center justify-center rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-700 transition-colors disabled:opacity-40"
                              data-testid={`grid-ask-btn-${item.name}`}
                            >
                              <ChatTeardropText size={9} />
                            </button>
                          )}
                          {previousVersions[item.id] && (
                            <button
                              title={`${previousVersions[item.id].count} previous version(s)`}
                              onClick={() => openPreviousVersions(item)}
                              className="w-5 h-5 flex items-center justify-center rounded bg-amber-100 hover:bg-amber-200 text-amber-700 transition-colors"
                              data-testid={`grid-versions-btn-${item.name}`}
                            >
                              <ClockCounterClockwise size={9} />
                            </button>
                          )}
                          {previousVersions[item.id] && (
                            <button
                              title="Compare to previous version"
                              onClick={() => handleCompareToPrevoius(item)}
                              disabled={!!comparingId}
                              className="w-5 h-5 flex items-center justify-center rounded bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors disabled:opacity-40"
                              data-testid={`grid-compare-btn-${item.name}`}
                            >
                              {comparingId === item.id
                                ? <ArrowsClockwise size={9} className="animate-spin" />
                                : <ArrowsLeftRight size={9} />}
                            </button>
                          )}
                          {archiveContext && !item.folder && (
                            <button
                              title="Restore as current drawing"
                              onClick={() => restoreArchivedVersion(item)}
                              disabled={!!restoringId}
                              className="w-5 h-5 flex items-center justify-center rounded bg-violet-100 hover:bg-violet-200 text-violet-700 transition-colors disabled:opacity-40"
                              data-testid={`grid-restore-btn-${item.name}`}
                            >
                              <ArrowsLeftRight size={9} />
                            </button>
                          )}
                          {perms.canOpen && item.webUrl && (
                            <button title="Open in OneDrive" onClick={() => openItem(item)} className="w-5 h-5 flex items-center justify-center rounded bg-slate-200 hover:bg-slate-300 text-slate-600 transition-colors" data-testid={`grid-open-btn-${item.name}`}>
                              <ArrowSquareOut size={9} />
                            </button>
                          )}
                          {perms.canDownload && (
                            <button
                              title="Download"
                              onClick={async () => {
                                try {
                                  const resp = await downloadFileItem(item.id);
                                  const blob = new Blob([resp.data], { type: resp.headers["content-type"] || "application/octet-stream" });
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement("a");
                                  a.href = url; a.download = item.name; a.click();
                                  URL.revokeObjectURL(url);
                                } catch (e) { toast.error(`Download failed: ${e.response?.data?.detail || e.message}`); }
                              }}
                              className="w-5 h-5 flex items-center justify-center rounded bg-slate-200 hover:bg-slate-300 text-slate-600 transition-colors"
                              data-testid={`grid-download-btn-${item.name}`}
                            >
                              <DownloadSimple size={9} />
                            </button>
                          )}
                          {perms.canRename && (
                            <button title="Rename" onClick={() => startRename(item)} className="w-5 h-5 flex items-center justify-center rounded bg-slate-200 hover:bg-slate-300 text-slate-600 transition-colors" data-testid={`grid-rename-btn-${item.name}`}>
                              <PencilSimple size={9} />
                            </button>
                          )}
                          {perms.canMove && (
                            <button title="Move" onClick={() => setMovingItem(item)} className="w-5 h-5 flex items-center justify-center rounded bg-slate-200 hover:bg-slate-300 text-slate-600 transition-colors" data-testid={`grid-move-btn-${item.name}`}>
                              <ArrowsLeftRight size={9} />
                            </button>
                          )}
                          {perms.canDelete && (
                            <button title="Delete" onClick={() => setConfirmDeleteId(item.id)} className="w-5 h-5 flex items-center justify-center rounded bg-red-100 hover:bg-red-200 text-red-600 transition-colors" data-testid={`grid-delete-btn-${item.name}`}>
                              <Trash size={9} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        ) : (

          /* ── LIST VIEW ────────────────────────────────────────────────────── */
          <div className="bg-white border border-slate-200" data-testid="files-table">
            {/* Table Header */}
            <div className="flex items-center px-4 py-2 bg-slate-50 border-b border-slate-100 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              <span className="flex-1">Name</span>
              <span className="w-20 text-right hidden sm:block">Size</span>
              <span className="w-32 text-right hidden md:block">Modified</span>
              <span className="w-64 text-right">Actions</span>
            </div>

            {displayItems.length === 0 ? (
              <div className="py-16 text-center" data-testid="empty-folder-state">
                <FolderOpen size={32} className="text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-500">This folder is empty</p>
                <p className="text-xs text-slate-400 mt-1">Upload a file to get started.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {displayItems.map((item) => {
                  const { Icon, cls } = fileIcon(item);
                  const perms = permissions(item, depth, parentName, completedOffset);
                  const isRenaming = renamingId === item.id;
                  const isDeleting = deletingId === item.id;
                  const isConfirmingDelete = confirmDeleteId === item.id;

                  return (
                    <div
                      key={item.id}
                      className={`flex items-center px-4 py-2.5 hover:bg-slate-50 group transition-colors
                        ${draggingItem && item.folder && item.id !== draggingItem?.id && dragOverId === item.id ? "bg-violet-50 ring-1 ring-inset ring-violet-300" : ""}
                        ${draggingItem && item.id === draggingItem?.id ? "opacity-40" : ""}
                      `}
                      data-testid={`item-row-${item.name}`}
                      draggable={perms.canMove}
                      onDragStart={(e) => { e.stopPropagation(); setDraggingItem(item); e.dataTransfer.effectAllowed = "move"; }}
                      onDragEnd={() => { setDraggingItem(null); setDragOverId(null); }}
                      onDragOver={(e) => { if (draggingItem && item.folder && item.id !== draggingItem?.id) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverId(item.id); } }}
                      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverId(null); }}
                      onDrop={(e) => { e.preventDefault(); if (draggingItem && item.folder && item.id !== draggingItem?.id) handleDragDrop(item); setDragOverId(null); }}
                    >
                      {/* Icon + Name */}
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <Icon size={17} className={`flex-shrink-0 ${cls}`} />

                        {isRenaming ? (
                          <input
                            autoFocus
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.preventDefault(); commitRename(item); }
                              if (e.key === "Escape") cancelRename();
                            }}
                            onBlur={() => commitRename(item)}
                            className="flex-1 min-w-0 text-sm font-medium text-slate-800 border border-primary rounded-sm px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary/30"
                            data-testid={`rename-input-${item.name}`}
                          />
                        ) : item.folder ? (
                          <button
                            onClick={() => navigateInto(item)}
                            className="text-sm font-medium text-slate-800 hover:text-primary hover:underline text-left truncate"
                            data-testid={`folder-link-${item.name}`}
                          >
                            {item.name}
                          </button>
                        ) : (
                          <button
                            onDoubleClick={() => handleFileOpen(item)}
                            title="Double-click to open"
                            className="text-sm font-medium text-slate-800 hover:text-primary text-left truncate cursor-pointer group/file"
                            data-testid={`file-link-${item.name}`}
                          >
                            {item.name}
                            <span className="ml-1.5 text-[10px] text-slate-300 group-hover/file:text-slate-400 hidden group-hover/file:inline">
                              double-click to open
                            </span>
                          </button>
                        )}

                        {/* Protection badge */}
                        {perms.badge === "project" && (
                          <span className="flex-shrink-0 flex items-center gap-0.5 text-[9px] font-semibold text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded-sm uppercase tracking-wide hidden lg:flex">
                            <Lock size={8} /> Project
                          </span>
                        )}
                        {perms.badge === "protected" && (
                          <span className="flex-shrink-0 flex items-center gap-0.5 text-[9px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm uppercase tracking-wide hidden lg:flex">
                            <Lock size={8} /> Protected
                          </span>
                        )}
                        {perms.badge === "system" && (
                          <span className="flex-shrink-0 flex items-center gap-0.5 text-[9px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm uppercase tracking-wide hidden lg:flex">
                            <Lock size={8} /> System
                          </span>
                        )}
                      </div>

                      {/* Size */}
                      <span className="w-20 text-right text-xs text-slate-400 hidden sm:block flex-shrink-0">
                        {item.folder ? "—" : fmtSize(item.size)}
                      </span>

                      {/* Modified */}
                      <span className="w-32 text-right text-xs text-slate-400 hidden md:block flex-shrink-0">
                        {fmtDate(item.lastModifiedDateTime)}
                      </span>

                      {/* Actions */}
                      <div className="w-64 flex items-center justify-end gap-0.5 flex-shrink-0">
                        {isDeleting ? (
                          <ArrowsClockwise size={14} className="text-slate-400 animate-spin" />
                        ) : analyzingId === item.id ? (
                          <div className="flex items-center gap-1.5 text-xs text-emerald-600">
                            <ArrowsClockwise size={13} className="animate-spin" />
                            <span className="text-[11px]">Loading…</span>
                          </div>
                        ) : isConfirmingDelete ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDelete(item)}
                              className="text-[11px] text-red-600 font-semibold hover:underline"
                              data-testid={`confirm-delete-${item.name}`}
                            >Delete</button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="text-[11px] text-slate-400 hover:underline ml-1"
                            >Cancel</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            {/* Ask about drawing — PDF/image files only */}
                            {isAnalyzable(item) && (
                              <button
                                title="Ask a question about this drawing"
                                onClick={() => handleAskAboutDrawing(item)}
                                disabled={!!analyzingId}
                                className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 rounded transition-colors disabled:opacity-40"
                                data-testid={`ask-drawing-btn-${item.name}`}
                              >
                                <ChatTeardropText size={11} />
                                Ask
                              </button>
                            )}
                            {/* Previous versions — only rendered when the
                                backend confirmed this drawing has an
                                archived version available. */}
                            {previousVersions[item.id] && (
                              <button
                                title={`View ${previousVersions[item.id].count} previous version${previousVersions[item.id].count === 1 ? "" : "s"} of "${item.name}"`}
                                onClick={() => openPreviousVersions(item)}
                                className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 rounded transition-colors"
                                data-testid={`previous-versions-btn-${item.name}`}
                              >
                                <ClockCounterClockwise size={11} weight="bold" />
                                {previousVersions[item.id].count}
                              </button>
                            )}
                            {/* Compare to previous version */}
                            {previousVersions[item.id] && (
                              <button
                                title={`Compare "${item.name}" with its most recent previous version`}
                                onClick={() => handleCompareToPrevoius(item)}
                                disabled={!!comparingId}
                                className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 rounded transition-colors disabled:opacity-40"
                                data-testid={`compare-previous-btn-${item.name}`}
                              >
                                {comparingId === item.id
                                  ? <ArrowsClockwise size={11} className="animate-spin" />
                                  : <ArrowsLeftRight size={11} weight="bold" />}
                                Compare
                              </button>
                            )}
                            {/* Restore — only inside an archive folder, only on files */}
                            {archiveContext && !item.folder && (
                              <button
                                title={`Restore "${item.name}" as the current drawing — the existing current drawing will be archived here.`}
                                onClick={() => restoreArchivedVersion(item)}
                                disabled={!!restoringId}
                                className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 hover:bg-violet-100 hover:border-violet-300 rounded transition-colors disabled:opacity-40"
                                data-testid={`restore-version-btn-${item.name}`}
                              >
                                {restoringId === item.id
                                  ? <ArrowsClockwise size={11} className="animate-spin" />
                                  : <ArrowsLeftRight size={11} weight="bold" />}
                                Restore
                              </button>
                            )}
                            {/* Open */}
                            {perms.canOpen && item.webUrl && (
                              <ActionBtn icon={ArrowSquareOut} label="Open in OneDrive" onClick={() => openItem(item)} testId={`open-btn-${item.name}`} />
                            )}
                            {/* Download (files only) */}
                            {perms.canDownload && (
                              <ActionBtn icon={DownloadSimple} label="Download" onClick={async () => {
                                try {
                                  const resp = await downloadFileItem(item.id);
                                  const blob = new Blob([resp.data], { type: resp.headers["content-type"] || "application/octet-stream" });
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement("a");
                                  a.href = url; a.download = item.name; a.click();
                                  URL.revokeObjectURL(url);
                                } catch (e) { toast.error(`Download failed: ${e.response?.data?.detail || e.message}`); }
                              }} testId={`download-btn-${item.name}`} />
                            )}
                            {/* Rename */}
                            {perms.canRename && (
                              <ActionBtn icon={PencilSimple} label="Rename" onClick={() => startRename(item)} testId={`rename-btn-${item.name}`} />
                            )}
                            {/* Move */}
                            {perms.canMove && (
                              <ActionBtn icon={ArrowsLeftRight} label="Move" onClick={() => setMovingItem(item)} testId={`move-btn-${item.name}`} />
                            )}
                            {/* Delete */}
                            {perms.canDelete && (
                              <ActionBtn icon={Trash} label="Delete" onClick={() => setConfirmDeleteId(item.id)} testId={`delete-btn-${item.name}`} danger />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      </div>{/* end main column */}

      {/* ── AI Chat Sidebar ─────────────────────────────────────────────────── */}
      <FileAssistantSidebar
        chatOpen={chatOpen}
        setChatOpen={setChatOpen}
        chatMessages={chatMessages}
        chatLoading={chatLoading}
        chatInput={chatInput}
        setChatInput={setChatInput}
        handleSendChat={handleSendChat}
        handleClearChat={handleClearChat}
        chatEndRef={chatEndRef}
        onDelete={(action) => setPendingDeleteAction(action)}
        onMove={(action) => setPendingMoveAction(action)}
        onCreate={(action) => handleAIConfirmCreateFolder(action)}
        onAddContractor={(action) => setContractorContactModal({ action, contactName: "", contactEmail: "" })}
        onModifyLayout={handleAIModifyLayout}
        onBrowse={handleBrowseAssistantResult}
      />

      {/* Move folder picker modal (manual + AI-suggested without direct destination) */}
      {(movingItem || (pendingMoveAction && !pendingMoveAction.destination_folder_id)) && ddRootId && (
        <MoveFolderPicker
          rootFolderId={ddRootId}
          movingItemId={movingItem?.id || pendingMoveAction?.item_id}
          currentParentId={currentFolderId}
          onMove={movingItem ? handleMove : handleAIMoveConfirm}
          onClose={() => { setMovingItem(null); setPendingMoveAction(null); }}
        />
      )}

      {/* AI direct move confirm (AI already knows destination folder ID) */}
      {pendingMoveAction?.destination_folder_id && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="ai-direct-move-modal"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5">
            <div className="flex items-center gap-2.5 mb-3">
              <ArrowsLeftRight size={16} className="text-blue-500 flex-shrink-0" />
              <h3 className="text-sm font-bold text-slate-800">Confirm Move</h3>
            </div>
            <p className="text-sm text-slate-600 mb-1">
              Move <span className="font-semibold text-slate-800">“{pendingMoveAction.item_name}”</span>
            </p>
            <p className="text-sm text-slate-500 mb-4">
              → <span className="font-semibold text-blue-600">{pendingMoveAction.destination_folder_name || "Selected folder"}</span>
            </p>
            {pendingMoveAction.reason && (
              <p className="text-xs text-slate-400 mb-4 italic">“{pendingMoveAction.reason}”</p>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setPendingMoveAction(null)}
                className="text-sm text-slate-600 hover:text-slate-800 px-3 py-1.5 border border-slate-200 rounded transition-colors"
                data-testid="ai-direct-move-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleAIDirectMove}
                className="text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 px-4 py-1.5 rounded transition-colors"
                data-testid="ai-direct-move-confirm-btn"
              >
                Move
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Smart Upload Modal */}
      {smartUploadOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) { setSmartUploadOpen(false); setSmartUploadFiles([]); } }}
          data-testid="smart-upload-modal"
        >
          <div className="bg-white border border-slate-200 shadow-2xl w-full max-w-lg rounded-sm overflow-hidden">
            {/* Project selector header */}
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkle size={15} className="text-violet-500" weight="fill" />
                  <h2 className="text-sm font-bold text-slate-800">Smart Upload</h2>
                </div>
                <button
                  onClick={() => { setSmartUploadOpen(false); setSmartUploadFiles([]); }}
                  className="text-slate-400 hover:text-slate-700 p-1 rounded transition-colors"
                  data-testid="smart-upload-modal-close-btn"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Target Project
                </label>
                {currentProject && smartUploadProject?.id === currentProject.id && (
                  <span
                    className="text-[10px] font-semibold text-violet-600 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-sm"
                    data-testid="smart-upload-auto-detected-badge"
                  >
                    Auto-detected from current folder
                  </span>
                )}
              </div>
              <select
                value={smartUploadProject?.id || ""}
                onChange={(e) => {
                  const p = projects.find((p) => p.id === e.target.value) || null;
                  setSmartUploadProject(p);
                }}
                className="w-full text-sm border border-slate-200 rounded-sm px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400 text-slate-800"
                data-testid="smart-upload-project-selector"
              >
                <option value="">— Select a project —</option>
                {projects
                  .filter((p) => p.onedrive_folder_id)
                  .map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
              </select>
              {projects.filter((p) => p.onedrive_folder_id).length === 0 && (
                <p className="text-[10px] text-amber-600 mt-1.5">
                  No projects with linked OneDrive folders. Create a project and link a folder first.
                </p>
              )}
              {!currentProject && !smartUploadProject && (
                <p className="text-[10px] text-slate-400 mt-1.5">
                  Navigate into a project folder first for automatic project detection.
                </p>
              )}
            </div>

            {/* Panel — only show when a project is chosen */}
            {smartUploadProject ? (
              <SmartUploadPanel
                project={smartUploadProject}
                initialFiles={smartUploadFiles}
                onDone={(results) => {
                  // Refresh the current folder view if the user is browsing this project
                  if (currentFolderId) loadFolder(currentFolderId);
                  setSmartUploadFiles([]);
                }}
              />
            ) : (
              <div className="px-5 py-10 text-center">
                <p className="text-sm text-slate-400">Select a project above to continue.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI Delete Confirmation Modal */}
      {pendingDeleteAction && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="ai-delete-modal">
          <div className="bg-white border border-slate-200 shadow-xl max-w-sm w-full mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-9 h-9 rounded-full bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
                <Trash size={16} className="text-red-500" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 text-sm mb-1">Confirm Delete</h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  AI Assistant suggests deleting{" "}
                  <span className="font-semibold text-slate-800">“{pendingDeleteAction.item_name}”</span>.
                </p>
                {pendingDeleteAction.reason && (
                  <p className="text-xs text-slate-500 mt-1 italic">Reason: {pendingDeleteAction.reason}</p>
                )}
                <p className="text-xs text-red-600 font-medium mt-2">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPendingDeleteAction(null)}
                data-testid="ai-delete-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white border-red-600"
                onClick={handleAIConfirmDelete}
                data-testid="ai-delete-confirm-btn"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Contractor Contact Details Modal */}
      {contractorContactModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="contractor-contact-modal">
          <div className="bg-white border border-slate-200 shadow-xl max-w-sm w-full mx-4 p-6 rounded-md">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-9 h-9 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-shrink-0">
                <UserPlus size={16} className="text-emerald-600" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-slate-900 text-sm mb-0.5">
                  Add Contractor: {contractorContactModal.action.contractor_name}
                </h3>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Optionally add a contact person for this discipline. You can also skip and add contacts later from Project Settings.
                </p>
              </div>
            </div>
            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Contact Name <span className="text-slate-400 font-normal">(optional)</span></label>
                <input
                  type="text"
                  placeholder="e.g. John Smith"
                  value={contractorContactModal.contactName}
                  onChange={(e) => setContractorContactModal((m) => ({ ...m, contactName: e.target.value }))}
                  className="w-full text-sm border border-slate-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400"
                  data-testid="contractor-contact-name-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Contact Email <span className="text-slate-400 font-normal">(optional)</span></label>
                <input
                  type="email"
                  placeholder="e.g. john@mechco.com"
                  value={contractorContactModal.contactEmail}
                  onChange={(e) => setContractorContactModal((m) => ({ ...m, contactEmail: e.target.value }))}
                  className="w-full text-sm border border-slate-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400"
                  data-testid="contractor-contact-email-input"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setContractorContactModal(null)}
                data-testid="contractor-contact-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAIConfirmAddContractor(contractorContactModal.action, "", "")}
                data-testid="contractor-contact-skip-btn"
              >
                Skip &amp; Add
              </Button>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600"
                onClick={() => handleAIConfirmAddContractor(
                  contractorContactModal.action,
                  contractorContactModal.contactName,
                  contractorContactModal.contactEmail,
                )}
                data-testid="contractor-contact-confirm-btn"
              >
                Add Contractor
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Email Preview Slide-Over */}
      {emailPreview.open && (
        <div className="fixed inset-0 z-50 flex" data-testid="email-preview-overlay">
          <div className="flex-1 bg-black/40" onClick={closeEmailPreview} />
          <div className="w-full max-w-2xl bg-white shadow-2xl flex flex-col h-full border-l border-slate-200">
            <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 flex-shrink-0 bg-slate-50">
              <EnvelopeOpen size={16} className="text-slate-500 flex-shrink-0" weight="fill" />
              <span className="text-sm font-medium text-slate-800 truncate flex-1 min-w-0" title={emailPreview.filename}>
                {emailPreview.filename}
              </span>
              <button
                onClick={closeEmailPreview}
                className="text-slate-400 hover:text-slate-700 transition-colors flex-shrink-0"
                title="Close preview"
                data-testid="email-preview-close"
              >
                <X size={16} weight="bold" />
              </button>
            </header>
            <div className="flex-1 overflow-hidden">
              {emailPreview.loading ? (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-400">
                  <CircleNotch size={28} className="animate-spin" />
                  <span className="text-sm">Loading email…</span>
                </div>
              ) : (
                <iframe
                  srcDoc={emailPreview.html}
                  className="w-full h-full border-0"
                  sandbox="allow-same-origin"
                  title={emailPreview.filename}
                  data-testid="email-preview-iframe"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Small action button helper ───────────────────────────────────────────────

function ActionBtn({ icon: Icon, label, onClick, testId, danger }) {
  return (
    <button
      title={label}
      onClick={onClick}
      className={`p-1.5 rounded transition-colors ${
        danger
          ? "text-slate-400 hover:text-red-500 hover:bg-red-50"
          : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"
      }`}
      data-testid={testId}
    >
      <Icon size={14} />
    </button>
  );
}
